import { useDisclosure, Link, Button } from "@heroui/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaLockOpen, FaPencil, FaPlus } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import { api } from "@/api";
import useErrorHandling from "@/hooks/useErrorHandling";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { LockPasscode } from "@/api/locks/locks.ts";
import { EditPasscodeModal } from "@/app/components/modals/lock/editPasscodeModal";
import { useAppSelector } from "@/hooks/useAppSelector";
import { CreatePasscodeModal } from "@/app/components/modals/lock/createPasscodeModal";

export function LockPasscodesPage() {
  const { id } = useParams() as { id: string };
  const [passcodes, setPasscodes] = useState<LockPasscode[] | null>(null);
  const [passcode, setPasscode] = useState<LockPasscode | null>(null);

  const handleError = useErrorHandling();

  const createPasscodeModalDisclosure = useDisclosure();
  const editPasscodeModalDisclosure = useDisclosure();

  const offices = useAppSelector((state) => state.handbooks.office);
  const office = useMemo(() => {
    if (Number(id) === -1) {
      return null;
    }
    return offices.find((office) => office.id === Number(id));
  }, [offices, id]);

  const editPasscode = (passcode: LockPasscode) => {
    setPasscode(passcode);
    editPasscodeModalDisclosure.onOpen();
  };

  useEffect(() => {
    api.locks
      .passcodes(Number(id))
      .then((response) => {
        setPasscodes(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [editPasscodeModalDisclosure.isOpen]);

  return (
    <>
      <CreatePasscodeModal
        disclosure={createPasscodeModalDisclosure}
        lock={+id}
      />
      <EditPasscodeModal
        disclosure={editPasscodeModalDisclosure}
        lock={+id}
        passcode={passcode}
      />
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex flex-col gap-2 max-md:flex-grow">
          <Link
            onClick={(_e) => {
              api.locks
                .unlock(office?.id || 0)
                .then((_res) => toast.success("Замок успешно открыт!"));
            }}
            className="flex flex-row gap-2 items-center p-4 rounded-xl font-medium text-foreground-600 cursor-pointer">
            <FaLockOpen />
            Открыть замок
          </Link>
          <Link
            href={`/dashboard/lock/${id}`}
            className="flex flex-row items-center p-4 rounded-xl font-medium text-foreground-600">
            Журнал действий
          </Link>
          <Link
            href={`/dashboard/lock/${id}/passcodes`}
            className="flex flex-row items-center p-4 rounded-xl font-medium text-primary bg-primary bg-opacity-10">
            Пароли
          </Link>
        </div>
        <div className="flex-grow flex flex-col gap-2">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            <div className="flex flex-row w-full items-center">
              <div className="flex flex-row justify-between w-full gap-4 items-center">
                <div className="flex flex-row items-center gap-2">
                  <span className="font-medium">Замок</span>
                </div>
                <Button
                  startContent={<FaPlus />}
                  size="sm"
                  color="primary"
                  variant="solid"
                  className="max-w-fit"
                  onClick={() => createPasscodeModalDisclosure.onOpen()}>
                  Создать пароль
                </Button>
              </div>
            </div>
            <TableBuilder
              removeWrapper
              displayOptions={false}
              columns={[
                {
                  label: "Пароль",
                  key: "passcode",
                  type: ColumnType.String,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaPencil />,
                      onClick: (_value, row) => {
                        editPasscode(row);
                      },
                    },
                  ],
                },
              ]}
              data={passcodes || []}
              rowsPerPage={10}
            />
          </div>
        </div>
      </div>
    </>
  );
}
